/**
 * Queue table (§18, §30) — the scannability centerpiece. Presentational: the
 * container passes the ordered/visible column model, the server-ordered rows, the
 * active sort + handlers. Responsibilities here:
 *   - render the data-driven column model (Tasks 2/3/6) — dense, aligned cells;
 *   - sortable headers (Task 2): clickable, keyboard-operable, asc/desc caret +
 *     an aria-sort + active-sort styling. Express-first is the server default
 *     until a header is clicked (the container owns the sort state);
 *   - express/priority rows carry a left accent edge + a bolt (the express-first
 *     sort made obvious at a glance);
 *   - full keyboard nav: ↑/↓ or j/k move the row, Enter opens (index math is the
 *     pure lib/keyboardNav); roving tabindex + visible focus ring;
 *   - sticky header; the table renders every row of the (≤200/page) result set
 *     and the PAGE scrolls (full-page Shopify-style) — no inner scroll box.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { OrderRow } from "../../lib/api.js";
import { isPriorityClass, isPackable } from "../../lib/status.js";
import { moveSelection } from "../../lib/keyboardNav.js";
import { Button, Checkbox } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";
import type { QueueColumn } from "./columns.js";

export interface QueueSort {
  key: string;
  dir: "asc" | "desc";
}

/** Bulk-select wiring (Task 4); when present, the table shows a checkbox column. */
export interface QueueSelection {
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Toggle a set of ids at once (the header select-all over the current page). */
  setMany: (ids: string[], on: boolean) => void;
  allOf: (ids: string[]) => boolean;
  someOf: (ids: string[]) => boolean;
}

export function OrderTable({
  orders,
  columns,
  selectedId,
  onOpen,
  onPack,
  sort,
  onSort,
  selection,
  now,
}: {
  orders: OrderRow[];
  /** The ordered, visible columns (the container resolves visibility/order). */
  columns: QueueColumn[];
  selectedId?: string | null;
  onOpen: (id: string) => void;
  /** Task 1 quick-pack: send a single order straight to the pack station. */
  onPack?: (id: string) => void;
  /** Active sort (Task 2); null = server express-first default. */
  sort: QueueSort | null;
  /** Toggle/select sort by a column's sortKey. */
  onSort: (sortKey: string) => void;
  /** Task 4 bulk-select; when present, the leading checkbox column is rendered. */
  selection?: QueueSelection;
  now: number;
}) {
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  // True only while DOM focus is inside the table — so background data changes
  // (the clamp effect below) never yank focus out of the filter bar into a row.
  const hasFocusRef = useRef(false);
  const [focusIndex, setFocusIndex] = useState<number>(-1);

  const len = orders.length;

  // Clamp focus when the list changes underneath us.
  useEffect(() => {
    if (focusIndex >= len) setFocusIndex(len - 1);
  }, [len, focusIndex]);

  // Move DOM focus to the focused row — only while the table already owns focus.
  useLayoutEffect(() => {
    if (focusIndex < 0 || !hasFocusRef.current) return;
    const el = rowRefs.current.get(focusIndex);
    if (el) {
      el.focus();
      el.scrollIntoView({ block: "nearest" });
    }
  }, [focusIndex]);

  const rovingIndex = len === 0 ? -1 : Math.max(focusIndex, 0);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusIndex((i) => moveSelection(i, 1, len));
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusIndex((i) => moveSelection(i, -1, len));
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusIndex(len > 0 ? 0 : -1);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusIndex(len - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = focusIndex >= 0 ? focusIndex : rovingIndex;
        const row = target >= 0 ? orders[target] : undefined;
        if (row) onOpen(row.id);
      }
    },
    [len, focusIndex, rovingIndex, orders, onOpen],
  );

  const ctx = { now };
  const pageIds = orders.map((o) => o.id);

  return (
    <div className="table-card">
      <div className="table-scroll">
        <table className="q-table" aria-label="Order queue">
          <thead>
            <tr>
              {selection && (
                <th className="q-check">
                  <Checkbox
                    ariaLabel="Select all orders on this page"
                    checked={selection.allOf(pageIds)}
                    indeterminate={selection.someOf(pageIds)}
                    onChange={(on) => selection.setMany(pageIds, on)}
                  />
                </th>
              )}
              {columns.map((c) => {
                const active = sort != null && c.sortKey != null && sort.key === c.sortKey;
                const ariaSort = active ? (sort!.dir === "asc" ? "ascending" : "descending") : c.sortKey ? "none" : undefined;
                return (
                  <th
                    key={c.key}
                    className={(c.num ? "num" : "") + (active ? " q-th--sorted" : "")}
                    aria-sort={ariaSort}
                  >
                    {c.sortKey ? (
                      <button
                        type="button"
                        className="q-sort"
                        onClick={() => onSort(c.sortKey!)}
                        title={`Sort by ${c.header}`}
                      >
                        <span>{c.header}</span>
                        <Icon
                          name={active ? (sort!.dir === "asc" ? "chevronUp" : "chevronDown") : "chevronDown"}
                          className={"q-sort__caret" + (active ? " q-sort__caret--active" : "")}
                          size={13}
                        />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {onPack && <th aria-label="Actions"></th>}
            </tr>
          </thead>
          <tbody
            onKeyDown={onKeyDown}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hasFocusRef.current = false;
            }}
          >
            {orders.map((o, index) => {
              const priority = isPriorityClass(o.serviceClass);
              const selected = o.id === selectedId;
              return (
                <tr
                  key={o.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(index, el);
                    else rowRefs.current.delete(index);
                  }}
                  className={
                    "q-row" +
                    (selected ? " q-row--selected" : "") +
                    (priority ? " q-row--priority" : "") +
                    (selection?.isSelected(o.id) ? " q-row--checked" : "")
                  }
                  tabIndex={index === rovingIndex ? 0 : -1}
                  aria-current={selected ? "true" : undefined}
                  onClick={() => {
                    hasFocusRef.current = true;
                    setFocusIndex(index);
                    onOpen(o.id);
                  }}
                  onFocus={() => {
                    hasFocusRef.current = true;
                    setFocusIndex(index);
                  }}
                >
                  {selection && (
                    <td className="q-check">
                      <Checkbox
                        ariaLabel={`Select order ${o.name}`}
                        checked={selection.isSelected(o.id)}
                        onChange={() => selection.toggle(o.id)}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className={c.num ? "num" : undefined}>
                      {c.cell(o, ctx)}
                    </td>
                  ))}
                  {onPack && (
                    <td className="q-actions">
                      {isPackable(o.status) && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon="package"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPack(o.id);
                          }}
                        >
                          Pack
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
